import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import debounce from 'lodash.debounce'
import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
  where,
} from 'firebase/firestore'
import { firebase_firestore } from '../common/firebaseConfig'
import { Pattern } from '../app/builder/contexts/PatternContext'
import * as jsondiffpatch from 'jsondiffpatch'
import ReactGA from 'react-ga4'
import { SnackbarHelperContext } from '../contexts/SnackbarHelperContext'
import { ConditionGroupType } from '../app/builder/contexts/PatternTreeContext'
import murmurhash from 'murmurhash'
import { useModalContext } from '../contexts/ModalProvider'

// Helper to handle null/undefined values for Firebase
const undefinedToNull = (obj: any) => {
  Object.keys(obj).forEach((key) => {
    if (obj[key] === undefined) {
      obj[key] = null
    } else if (typeof obj[key] === 'object' && obj[key] !== null) {
      undefinedToNull(obj[key])
    }
  })
  return obj
}

// Function to create a hash from a pattern
// We normalize the data structure to ensure consistent hashing regardless of key/array ordering
export const hashPattern = (pattern: Pattern): number => {
  // Create a deep copy to avoid modifying the original
  const patternCopy = JSON.parse(JSON.stringify(pattern))

  // Exclude fields that don't reflect actual user changes
  const { version, alertVersion, ...contentFields } = patternCopy

  // Normalize the data structure recursively
  // parentKey is the object key under which this value lives, used to decide sort behavior
  const normalizeObject = (obj: any, parentKey?: string): any => {
    if (obj === null || obj === undefined) {
      return obj
    }

    if (Array.isArray(obj)) {
      // conditions and conditionGroups are ordered by design — reordering them changes
      // backtest evaluation order, so we must preserve their order in the hash.
      // Other id-bearing arrays (e.g. prerequisites) are order-insensitive and can be sorted
      // for consistent hashing regardless of insertion order.
      const isOrderedArray = parentKey === 'conditions' || parentKey === 'conditionGroups'
      const hasIds =
        obj.length > 0 && typeof obj[0] === 'object' && obj[0] !== null && 'id' in obj[0]

      if (!isOrderedArray && hasIds) {
        return obj
          .map((item) => normalizeObject(item))
          .sort((a, b) => (a.id && b.id ? a.id.localeCompare(b.id) : 0))
      }

      return obj.map((item) => normalizeObject(item))
    }

    if (typeof obj === 'object') {
      const result: { [key: string]: any } = {}

      // Get all keys and sort them for consistent ordering
      const keys = Object.keys(obj).sort()

      // Process each key in sorted order
      for (const key of keys) {
        // Skip certain fields that don't affect pattern functionality
        if (['dropIndex'].includes(key)) {
          continue
        }

        result[key] = normalizeObject(obj[key], key)
      }

      return result
    }

    // For primitive values, return as is
    return obj
  }

  // Normalize the pattern's structure
  const normalizedPattern = normalizeObject(contentFields)

  // Convert to string with stable key ordering and hash
  return murmurhash.v3(JSON.stringify(normalizedPattern))
}

/**
 * Custom hook for saving patterns with autosave and force save capabilities
 */
export function useSavePattern(
  setPattern: (pattern: Pattern) => void,
  usermeta: any,
  tradingHouse: string,
  canViewPrivatePatterns: boolean
) {
  const { setSnackbarSettings } = useContext(SnackbarHelperContext)
  const { setIsOkayDialogOpen } = useModalContext()
  const [isSavingPattern, setSavingPattern] = useState(false)

  // Function to check if all conditions in the pattern are valid
  const isPatternValid = useCallback((patternToCheck: Pattern) => {
    // Get all conditions from buy and sell groups recursively
    const checkConditionsInGroup = (group: ConditionGroupType): boolean => {
      // Check all direct conditions
      for (const condition of group.conditions) {
        if (condition.condition.isValid === false) {
          return false
        }
      }

      // Check nested condition groups recursively
      for (const nestedGroup of group.conditionGroups) {
        if (!checkConditionsInGroup(nestedGroup)) {
          return false
        }
      }

      return true
    }

    // Check buy and sell condition groups
    for (const group of [
      ...patternToCheck.buyConditionGroups,
      ...patternToCheck.sellConditionGroups,
    ]) {
      if (!checkConditionsInGroup(group)) {
        return false
      }
    }

    return true
  }, [])

  // Function to check if a pattern has changed by comparing with its stored version
  const hasPatternChanged = useCallback(
    async (patternToCheck: Pattern, saveAsStandardPattern: boolean = false): Promise<boolean> => {
      // If there's no ID, it's a new pattern
      if (!patternToCheck.id) {
        return true
      }

      // A reconstructed-from-version-history pattern can be missing tradingHouse
      // (usePattern's reconstructToVersion doesn't carry it over) — bail out rather
      // than build a tradingHouses/undefined/standardPatterns/... path.
      if (saveAsStandardPattern && !patternToCheck.tradingHouse) {
        return true
      }

      try {
        // Determine pattern location based on type — must match savePatternImplementation's
        // own resolution, or this looks in the wrong collection for a standard pattern.
        // Since patternToCheck.id is guaranteed set here (early return above), an existing
        // standard pattern's own tradingHouse — not the admin's current context — is the
        // correct source of truth for where it actually lives.
        const patternLocation = saveAsStandardPattern
          ? `tradingHouses/${patternToCheck.tradingHouse}/standardPatterns`
          : `usermeta/${usermeta.gcid}/patterns`

        // Fetch the original pattern from Firebase
        const originalPatternDoc = await getDoc(
          doc(firebase_firestore, `${patternLocation}/${patternToCheck.id}`)
        )

        if (!originalPatternDoc.exists()) {
          return true // If the pattern doesn't exist in DB, consider it changed
        }

        const originalPattern = originalPatternDoc.data() as Pattern

        // Hash both patterns and compare
        const originalHash = hashPattern(originalPattern)
        const currentHash = hashPattern(patternToCheck)

        // If hashes are different, the pattern has changed
        return originalHash !== currentHash
      } catch (error) {
        console.error('Error checking if pattern changed:', error)
        // On error, assume pattern has changed to be safe
        return true
      }
    },
    [usermeta.gcid]
  )

  // Helper function to save pattern diffs
  const saveDiff = async (docRef: any, patternCopy: any) => {
    const existingDoc = await getDoc(doc(firebase_firestore, docRef.path))
    let previousPattern = {}

    if (existingDoc.exists()) {
      // Handle first version if needed
      const versionCollectionRef = await getDocs(
        query(collection(firebase_firestore, `${docRef.path}/versions`), orderBy('version'))
      ).then((querySnapshot) => querySnapshot.docs.map((doc) => doc.data()))

      if (!versionCollectionRef.length) {
        await setDoc(doc(firebase_firestore, `${docRef.path}/versions/1`), {
          version: 1,
          diff: JSON.stringify(jsondiffpatch.diff({}, existingDoc.data()!)),
          created: new Date().getTime(),
        })
      }

      previousPattern = existingDoc.data()!
    }

    const diff = jsondiffpatch.diff(previousPattern, patternCopy)
    if (diff) {
      const versionRef = doc(firebase_firestore, `${docRef.path}/versions/${patternCopy.version}`)
      const versionData = {
        version: patternCopy.version,
        diff: JSON.stringify(diff),
        created: new Date().getTime(),
      }
      await setDoc(versionRef, versionData)
    }
  }

  // The actual save implementation shared by both autosave and forceSave
  const savePatternImplementation = useCallback(
    async (patternToSave: Pattern, saveAsStandardPattern: boolean = false) => {
      if (!patternToSave || !usermeta) return

      // Check if the pattern has actually changed before saving
      const hasChanged = await hasPatternChanged(patternToSave, saveAsStandardPattern)
      if (!hasChanged) {
        return
      }

      try {
        setSavingPattern(true)
        // Promoting a personal pattern to standard (no id yet) targets the admin's current
        // trading house context — that's the intended "promote to whichever house I'm in
        // right now" behavior. Editing an EXISTING standard pattern in place (id already
        // set) must instead target that pattern's own tradingHouse, or an admin whose
        // context differs from the pattern's actual house would silently fork a brand-new
        // doc into the wrong house instead of updating the original (existingDoc lookup
        // below would miss, resetting version to 1) — the same source of truth
        // handleDeletePattern already uses via pattern.tradingHouse.
        const standardPatternTradingHouse =
          saveAsStandardPattern && patternToSave.id ? patternToSave.tradingHouse : tradingHouse

        if (saveAsStandardPattern && !standardPatternTradingHouse) {
          throw new Error(
            'Cannot save standard pattern: missing tradingHouse on the pattern being saved'
          )
        }

        let patternLocation = saveAsStandardPattern
          ? `tradingHouses/${standardPatternTradingHouse}/standardPatterns`
          : `usermeta/${usermeta.gcid}/patterns`

        let docRef = patternToSave.id
          ? doc(firebase_firestore, `${patternLocation}/${patternToSave.id}`)
          : doc(collection(firebase_firestore, patternLocation))

        const patternCopy = JSON.parse(JSON.stringify(patternToSave))
        undefinedToNull(patternCopy)

        let versions: any[] = []
        if (patternCopy.id) {
          // Check if pattern exists in the current location
          const existingDoc = await getDoc(
            doc(firebase_firestore, `${patternLocation}/${patternToSave.id}`)
          )

          if (!existingDoc.exists()) {
            patternCopy.version = 1
            patternCopy.alertVersion = 1
            docRef = doc(collection(firebase_firestore, patternLocation))
          } else {
            // Fetch versions to determine next version number
            const versionsQuery = query(
              collection(firebase_firestore, `${patternLocation}/${patternToSave.id}/versions`),
              orderBy('version')
            )
            const versionsSnapshot = await getDocs(versionsQuery)
            versions = versionsSnapshot.docs.map((doc) => doc.data())

            // Increment version
            const previousVersion =
              versions.length > 0 && versions[versions.length - 1].version
                ? versions[versions.length - 1].version
                : 1
            patternCopy.version = previousVersion + 1
            patternCopy.alertVersion = previousVersion + 1
          }
        } else {
          const userPatterns = await getCountFromServer(
            query(
              collection(firebase_firestore, `usermeta/${usermeta.gcid}/patterns`),
              where('active', '==', true)
            )
          )
          if (userPatterns.data().count > 200) {
            setIsOkayDialogOpen({
              title: '200 Pattern Limit Reached',
              description:
                'You have reached the maximum number of patterns. Please delete some patterns before creating a new one.',
            })
            return
          }

          patternCopy.version = 1
          patternCopy.alertVersion = 1
        }

        patternCopy.id = docRef.id
        patternCopy.active = true

        if (!saveAsStandardPattern) {
          patternCopy.gcid = usermeta.gcid

          if (usermeta.email && usermeta.email.includes('@')) {
            const userEmailPrefix = usermeta.email.split('@')[0]

            // Handle author field
            if (!patternCopy.author) {
              patternCopy.author = userEmailPrefix
            } else {
              if (patternCopy.author === 'Showcase' && !canViewPrivatePatterns) {
                patternCopy.author = 'Showcase'
              } else {
                patternCopy.author = userEmailPrefix
              }
            }
          }
        } else {
          patternCopy.author = standardPatternTradingHouse

          // Handle shared layouts
          if (patternCopy.layoutId && patternCopy.layoutId.trim !== '') {
            const existingLayout = await getDoc(
              doc(firebase_firestore, `usermeta/${usermeta.gcid}/layouts/${patternCopy.layoutId}`)
            )
            if (existingLayout.exists()) {
              const sharedLayoutsRef = doc(
                firebase_firestore,
                `sharedLayouts/${patternCopy.layoutId}`
              )
              await setDoc(sharedLayoutsRef, existingLayout.data()!)
            }
          }
        }

        patternCopy.tradingHouse = saveAsStandardPattern
          ? standardPatternTradingHouse
          : tradingHouse

        // Save diff for versioning
        await saveDiff(docRef, patternCopy)

        // Save the pattern
        await setDoc(docRef, patternCopy)

        // Update the pattern state
        setPattern(patternCopy)

        return patternCopy
      } catch (error) {
        console.error('Error saving pattern:', error)
        throw error
      } finally {
        setSavingPattern(false)
      }
    },
    [
      usermeta,
      tradingHouse,
      canViewPrivatePatterns,
      hasPatternChanged,
      setIsOkayDialogOpen,
      setPattern,
    ]
  )

  // Debounced save function with delay
  const debouncedSave = useRef(
    debounce(async (patternToSave: Pattern, saveAsStandardPattern: boolean = false) => {
      try {
        const savedPattern = await savePatternImplementation(patternToSave, saveAsStandardPattern)

        if (savedPattern) {
          ReactGA.event('pattern_autosave', {
            author: savedPattern.author,
            pattern_name: savedPattern.name,
          })
        }
      } catch (error) {
        console.error('Error auto-saving pattern:', error)
      }
    }, 3000)
  ).current

  // Function to trigger autosave with debounce
  const autosave = useCallback(
    (patternToSave: Pattern, saveAsStandardPattern: boolean = false) => {
      // Cancel any pending saves first to avoid race conditions
      debouncedSave.cancel()

      if (!patternToSave || !isPatternValid(patternToSave)) return

      // Schedule a new debounced save
      debouncedSave(patternToSave, saveAsStandardPattern)
    },
    [debouncedSave, isPatternValid]
  )

  // Function to force an immediate save
  const forceSave = useCallback(
    async (patternToSave: Pattern, saveAsStandardPattern: boolean = false) => {
      // Cancel any pending autosaves
      debouncedSave.cancel()

      if (!patternToSave || !isPatternValid(patternToSave)) return

      try {
        // Show saving notification
        setSnackbarSettings({
          message: 'Saving pattern...',
          severity: 'info',
          autoHideDuration: 3000,
        })

        const savedPattern = await savePatternImplementation(patternToSave, saveAsStandardPattern)

        if (savedPattern) {
          ReactGA.event('pattern_force_save', {
            author: savedPattern.author,
            pattern_name: savedPattern.name,
          })
          console.log('Pattern saved successfully!')

          // Show success notification
          setSnackbarSettings({
            message: 'Pattern saved successfully!',
            severity: 'success',
            autoHideDuration: 3000,
          })
        } else {
          setSnackbarSettings({
            message: 'No changes detected',
            severity: 'info',
            autoHideDuration: 6000,
          })
        }

        return savedPattern
      } catch (error) {
        console.error('Error forcing pattern save:', error)

        // Show error notification
        setSnackbarSettings({
          message: 'Error saving pattern',
          severity: 'error',
          autoHideDuration: 6000,
        })

        throw error
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [setSnackbarSettings, debouncedSave, isPatternValid, savePatternImplementation]
  )

  // Clean up the debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSave.cancel()
    }
  }, [debouncedSave])

  return {
    autosave,
    forceSave,
    cancelPendingSave: debouncedSave.cancel,
    isPatternValid,
    hasPatternChanged,
    isSavingPattern,
  }
}
